<template>
	<base-dialog>
		<div class="dialog-music-wrap" @click.self="close">
			<img :src="closeMusicIcon" class="dialog-close" alt="Закрыть" @click="close" />
			<!-- Обложка трека -->
			<img class="dialog-music-cover" :src="currentTrack.image_url || coverFallback" alt="" @error="onCoverError" />
			<!-- Название трека -->
			<p class="dialog-music-title">
				{{ currentTrack.title }}
			</p>

			<div class="progress-bar-wrap">
				<div class="progress-bar" @click="seekTrack">
					<div class="progress-bar-fill" :style="{ width: progressPercent + '%' }"></div>
					<div class="progress-bar-thumb" :style="{ left: progressPercent + '%' }"></div>
				</div>
				<div class="timing-wrap">
					<span class="timing-elapsed">{{ formatTime(elapsedTime) }}</span>
					<span class="timing-duration">{{ formatTime(totalDuration) }}</span>
				</div>
			</div>

			<div class="controls-wrap">
				<button class="control-btn" @click="rewind(-10)">
					<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
						<path
							d="M8.55585 20.3149C10.2004 20.9961 12.01 21.1743 13.7558 20.8271C15.5016 20.4798 17.1053 19.6226 18.364 18.364C19.6226 17.1053 20.4798 15.5016 20.8271 13.7558C21.1743 12.01 20.9961 10.2004 20.3149 8.55585C19.6337 6.91131 18.4802 5.50571 17.0001 4.51677C15.5201 3.52784 13.78 3 12 3C9.48395 3.00947 7.06897 3.99122 5.26 5.74L3 8M3 8V3M3 8H8"
							stroke="white"
							stroke-width="2"
							stroke-linecap="round"
							stroke-linejoin="round"
						/>
					</svg>
				</button>
				<button class="control-btn--main" @click="togglePlay">
					<svg
						v-if="isPlaying"
						xmlns="http://www.w3.org/2000/svg"
						width="18"
						height="24"
						viewBox="0 0 18 24"
						fill="none"
					>
						<path d="M3 3V21M15 3V21" stroke="white" stroke-width="6" stroke-linecap="round" />
					</svg>
					<svg v-else xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
						<path
							d="M3.75 3.75044C3.74992 3.48651 3.81948 3.22723 3.95166 2.99879C4.08384 2.77034 4.27396 2.58081 4.50281 2.44934C4.73167 2.31787 4.99116 2.24911 5.25509 2.25001C5.51902 2.2509 5.77804 2.32142 6.006 2.45444L15.0037 7.70294C15.2308 7.8347 15.4193 8.02375 15.5505 8.2512C15.6816 8.47864 15.7507 8.73652 15.7509 8.99905C15.7512 9.26158 15.6825 9.51957 15.5518 9.74724C15.421 9.97492 15.2328 10.1643 15.006 10.2964L6.006 15.5464C5.77804 15.6795 5.51902 15.75 5.25509 15.7509C4.99116 15.7518 4.73167 15.683 4.50281 15.5515C4.27396 15.4201 4.08384 15.2305 3.95166 15.0021C3.81948 14.7737 3.74992 14.5144 3.75 14.2504V3.75044Z"
							fill="white"
						/>
					</svg>
				</button>
				<button class="control-btn" @click="rewind(10)">
					<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
						<path
							d="M15.4442 20.3149C13.7996 20.9961 11.99 21.1743 10.2442 20.8271C8.49836 20.4798 6.89471 19.6226 5.63604 18.364C4.37737 17.1053 3.5202 15.5016 3.17293 13.7558C2.82566 12.01 3.00389 10.2004 3.68508 8.55585C4.36627 6.91131 5.51983 5.50571 6.99987 4.51677C8.47991 3.52784 10.22 3 12 3C14.516 3.00947 16.931 3.99122 18.74 5.74L21 8M21 8V3M21 8H16"
							stroke="white"
							stroke-width="2"
							stroke-linecap="round"
							stroke-linejoin="round"
						/>
					</svg>
				</button>
			</div>

			<audio
				ref="audioRef"
				:src="currentTrack.url"
				@timeupdate="onTimeUpdate"
				@loadedmetadata="onLoadedMetadata"
				@ended="onEnded"
			></audio>
		</div>
	</base-dialog>
</template>

<script setup lang="ts">
import BaseDialog from '../base/BaseDialog.vue'
import { MusicTrackObject } from '@/types'
import { computed, onMounted, ref, onUnmounted } from 'vue'
import closeMusicIcon from '@/resources/icons/music/close_music.svg'
import coverFallback from '@/resources/images/music/cover-fallback.png'

const audioRef = ref<HTMLAudioElement | null>(null)
const isPlaying = ref(false)
const totalDuration = ref(0)
const elapsedTime = ref(0)

const props = defineProps<{
	tracks: MusicTrackObject[]
	trackIndex?: number
}>()

const emit = defineEmits<{
	close: []
}>()

const currentTrack = computed(() => {
	const index = props.trackIndex ?? 0
	return props.tracks[index]
})

const progressPercent = computed(() => {
	if (totalDuration.value === 0) return 0
	return (elapsedTime.value / totalDuration.value) * 100
})

function togglePlay() {
	if (!audioRef.value) return

	if (isPlaying.value) {
		audioRef.value.pause()
	} else {
		audioRef.value.play()
	}

	isPlaying.value = !isPlaying.value
}

function onTimeUpdate() {
	if (audioRef.value) {
		elapsedTime.value = audioRef.value.currentTime
	}
}

function onLoadedMetadata() {
	if (audioRef.value) {
		totalDuration.value = audioRef.value.duration
	}
}

function onEnded() {
	isPlaying.value = false
	elapsedTime.value = 0
}

function seekTrack(event: MouseEvent) {
	if (!audioRef.value || totalDuration.value === 0) return

	const target = event.target as HTMLElement
	const bar = target.closest('.progress-bar')
	if (!bar) return

	const rect = bar.getBoundingClientRect()
	const clickX = event.clientX - rect.left
	const width = rect.width
	const seekTime = (clickX / width) * totalDuration.value

	audioRef.value.currentTime = seekTime
}

function formatTime(seconds: number): string {
	const mins = Math.floor(seconds / 60)
	const secs = Math.floor(seconds % 60)
	return `${mins}:${secs.toString().padStart(2, '0')}`
}

function onCoverError(e: Event) {
	const img = e.target as HTMLImageElement
	if (!img.src.endsWith(coverFallback)) img.src = coverFallback
}

function close() {
	if (audioRef.value) {
		audioRef.value.pause()
		audioRef.value.currentTime = 0
		audioRef.value.src = ''
		audioRef.value.load()
	}

	isPlaying.value = false
	elapsedTime.value = 0
	totalDuration.value = 0

	emit('close')
}

function rewind(seconds: number) {
	if (!audioRef.value) return
	audioRef.value.currentTime = Math.max(0, Math.min(audioRef.value.currentTime + seconds, totalDuration.value))
}

onMounted(() => {
	if (audioRef.value) {
		audioRef.value.play()
		isPlaying.value = true
	}
})

onUnmounted(() => {
	if (audioRef.value) {
		audioRef.value.pause()
		audioRef.value.src = ''
	}
})
</script>

<style lang="scss" scoped>
.dialog {
	&-music {
		&-wrap {
			display: flex;
			flex-direction: column;
			align-items: center;
			justify-content: flex-start;
			width: 100%;
			height: 100%;

			max-width: 500px;
			padding: 0 16px;
			margin-top: 80px;
			position: relative;
		}

		&-cover {
			width: 343px;
			max-width: 100%;
			aspect-ratio: 1 / 1;
			height: auto;
			margin-top: 34px;
			border-radius: var(--radius-md);
			object-fit: cover;
			flex-shrink: 0;
		}

		&-title {
			margin-top: 40px;
			color: var(--text-inverse);
			text-align: center;
			font: var(--font-heading);
		}
	}

	&-close {
		align-self: flex-end;
		cursor: pointer;
	}
}

.progress-bar-wrap {
	width: 100%;
	margin-top: 40px;
	display: flex;
	flex-direction: column;
	gap: 6px;
	justify-content: center;
}

.progress-bar {
	width: 100%;
	height: 2px;
	background-color: var(--overlay-light);
	position: relative;
	border-radius: 2px;
	cursor: pointer;

	&-fill {
		position: absolute;
		left: 0;
		top: 0;
		height: 100%;
		background-color: var(--text-inverse);
		transition: width 0.1s linear;
		border-radius: 2px;
	}

	&-thumb {
		position: absolute;
		left: 0;
		top: 50%;
		transform: translate(-50%, -50%);
		width: 8px;
		height: 8px;
		background-color: var(--text-inverse);
		border-radius: 50%;
		transition: left 0.1s linear;
	}
}

.timing-wrap {
	width: 100%;
	display: flex;
	justify-content: space-between;

	span {
		font: var(--font-caption);
		color: var(--text-inverse);
		opacity: 0.8;
	}
}

.controls-wrap {
	margin-top: 40px;
	display: flex;
	align-items: center;
	gap: 24px;
}

.control-btn--main {
	background-color: var(--action-primary);
	border: none;
	width: 63px;
	height: 63px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	aspect-ratio: 1/1;
	cursor: pointer;
}

.control-btn {
	background-color: transparent;
	border: none;
	width: 48px;
	height: 48px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	aspect-ratio: 1/1;
	cursor: pointer;
	color: var(--text-inverse);

	@media (hover: hover) {
		&:hover {
			background-color: color-mix(in srgb, var(--text-inverse) 10%, transparent);
		}
	}
}

.control-icon {
	width: 24px;
	height: 24px;
	fill: var(--text-inverse);
	flex-shrink: 0;

	svg {
		path {
			width: 24px;
			height: 24px;
		}
	}
}
</style>
